import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';
import { OrderDetailAttributes } from '@albatrosdeveloper/ave-models-package';
import { UnitMeasureDisplayComponent } from 'app/shared/components/unit-measure-display/unit-measure-display.component';

/**
 * Quantity Cell Renderer Component
 *
 * Reusable cell renderer for displaying multi-unit quantity information.
 * Shows:
 * - Sold quantity with unit of measure
 * - Base unit (UMB) quantity
 * - Parallel unit (UMP) quantity (conditional based on UMPActive flag)
 *
 * Usage in ColDef:
 * ```typescript
 * {
 *   field: 'soldQuantity',
 *   cellRenderer: QuantityCellRendererComponent,
 *   cellRendererParams: {
 *     UMPActive: true  // or false
 *   }
 * }
 * ```
 */
@Component({
  selector: "app-quantity-cell-renderer",
  standalone: true,
  imports: [CommonModule, DecimalPipe, UnitMeasureDisplayComponent],
  template: `
    <app-unit-measure-display
      [usedUnit]="{
        quantity: params?.data?.soldQuantity,
        unitId: params?.data?.soldUnitOfMeasure?._id,
        unitName: params?.data?.soldUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.soldUnitOfMeasure?.code,
      }"
      [baseUnit]="{
        quantity: params?.data?.baseQuantity,
        unitId: params?.data?.baseUnitOfMeasure?._id,
        unitName: params?.data?.baseUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.baseUnitOfMeasure?.code,
      }"
      [parallelUnit]="{
        quantity: params?.data?.pararellQuantity,
        unitId: params?.data?.pararellUnitOfMeasure?._id,
        unitName: params?.data?.pararellUnitOfMeasure?.name,
        unitAbbreviation: params?.data?.pararellUnitOfMeasure?.code,
      }"
      [material]="{
        _id: params?.data?.material?._id,
        code: params?.data?.material?.code,
        UMPActive: params?.data?.material?.UMPActive,
      }"
      [companyUMPActive]="isUMPActive"
      [statusId]="params.statusId"
    ></app-unit-measure-display>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuantityCellRendererComponent
  implements CellRenderer<OrderDetailAttributes, number>
{
  /**
   * Parameters from the grid containing the cell value and row data
   */
  @Input() params!: CellRendererParams<OrderDetailAttributes, number>;

  private readonly decimalPipe = new DecimalPipe("en-US");

  /**
   * Get UMPActive flag from params (spread from cellRendererParams)
   * SmartGrid spreads cellRendererParams directly into params object
   */
  get isUMPActive(): boolean {
    if (this.params?.getUMPActive) {
      const result = this.params.getUMPActive();
      return result ?? false;
    } else {
      return false;
    }
  }

  /**
   * Format sold quantity
   */
  get soldQuantity(): string {
    const value = this.params?.data?.soldQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  /**
   * Get sold unit of measure name
   */
  get soldUnitName(): string {
    return this.params?.data?.soldUnitOfMeasure?.name ?? "-";
  }

  /**
   * Format base quantity
   */
  get baseQuantity(): string {
    const value = this.params?.data?.baseQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  /**
   * Get base unit of measure name
   */
  get baseUnitName(): string {
    return this.params?.data?.baseUnitOfMeasure?.name ?? "-";
  }

  /**
   * Check if parallel unit exists
   */
  get hasParallelUnit(): boolean {
    return !!this.params?.data?.pararellUnitOfMeasure;
  }

  /**
   * Format parallel quantity
   */
  get parallelQuantity(): string {
    const value = this.params?.data?.pararellQuantity;
    return this.decimalPipe.transform(value, "1.3-3") ?? "0";
  }

  /**
   * Get parallel unit of measure name
   */
  get parallelUnitName(): string {
    return this.params?.data?.pararellUnitOfMeasure?.name ?? "-";
  }
}
